import type { Brand } from "@/store/slices/brands/brandsSlice";
import type { SelectionPartition } from "@/utils/selections/partitionSelections";
import type {
  BrandCheck,
  BrandSetCheck,
  FilterCheck,
  SegmentCheck,
} from "@/interfaces/selections/selections.types";
import {
  describeBlockedBrand,
  describeBlockedBrandSet,
  describeBlockedFilter,
  describeBlockedSegment,
} from "../describeBlocked";
import { displayCase } from "./displayCase";

export type RowState = "adoptable" | "included" | "blocked";
export type LedgerKind = "brandSet" | "segment" | "brand" | "filter";

export interface LedgerRow {
  id: string;
  kind: LedgerKind;
  name: string;
  meta?: string;
  state: RowState;
  statusLabel: string;
  /** Blocked rows only: the sentence beneath the name. */
  detail?: string;
  /** Adoptable and included composites only: revealed behind a chevron. */
  members?: string[];
  entitlement: boolean;
  /** ULID or code passed back to onAdd. Present iff state is "adoptable". */
  adoptId?: string;
  /** Brand rows only: handed straight to EntityIcon. */
  logo?: string;
  color?: string | null;
}

/** Store slices buildLedger reads through. Optional so tests may omit them. */
export interface BuildLedgerContext {
  /** activeGroup.brands.entities — the caller's own brand catalog. */
  brandEntities?: Record<string | number, Brand | undefined>;
}

/**
 * The API omits a name on some checks and returns an empty string on others,
 * so `??` alone leaves a row rendering as a bare icon with no label at all.
 */
const firstNonBlank = (...vals: (string | null | undefined)[]): string | undefined =>
  vals.find((v): v is string => typeof v === "string" && v.trim() !== "");

export interface LedgerGroup {
  kind: LedgerKind;
  label: string;
  note: string;
  collapsedByDefault: boolean;
  /** Set iff collapsedByDefault — the text of the expand button. */
  summary?: string;
  rows: LedgerRow[];
}

const NOUN: Record<LedgerKind, { one: string; many: string; group: string }> = {
  brandSet: { one: "brand set", many: "brand sets", group: "Brand sets" },
  segment: { one: "segment", many: "segments", group: "Segments" },
  brand: { one: "brand", many: "brands", group: "Brands" },
  filter: { one: "filter", many: "filters", group: "Filters" },
};

const countOf = (n: number, k: LedgerKind): string =>
  `${n} ${n === 1 ? NOUN[k].one : NOUN[k].many}`;

const setRow = (c: BrandSetCheck, state: RowState): LedgerRow => {
  const blocked = state === "blocked";
  const d = blocked ? describeBlockedBrandSet(c) : undefined;
  return {
    id: `brandSet:${c.ulid}`,
    kind: "brandSet",
    name: firstNonBlank(c.name) ?? "Brand set",
    meta: typeof c.memberCount === "number" ? countOf(c.memberCount, "brand") : undefined,
    state,
    statusLabel: state === "adoptable" ? "Add" : blocked ? d!.label : "Included",
    detail: d?.detail,
    members: blocked ? undefined : c.memberNames,
    entitlement: d?.entitlement ?? false,
    adoptId: state === "adoptable" ? c.ulid : undefined,
  };
};

const segmentRow = (c: SegmentCheck, state: RowState): LedgerRow => {
  const blocked = state === "blocked";
  const d = blocked ? describeBlockedSegment(c) : undefined;
  return {
    id: `segment:${c.code}`,
    kind: "segment",
    name: firstNonBlank(c.label) ?? "Segment",
    meta: c.filterLabels?.length ? countOf(c.filterLabels.length, "filter") : undefined,
    state,
    statusLabel: state === "adoptable" ? "Add" : blocked ? d!.label : "Included",
    detail: d?.detail,
    members: blocked ? undefined : c.filterLabels,
    entitlement: d?.entitlement ?? false,
    adoptId: state === "adoptable" ? c.code : undefined,
  };
};

const brandRow = (c: BrandCheck, state: RowState, ctx?: BuildLedgerContext): LedgerRow => {
  const d = state === "blocked" ? describeBlockedBrand(c) : undefined;
  // Prefer the caller's own catalog: it carries the logo and display name the
  // rest of the app renders, neither of which the validate response returns.
  const entity = ctx?.brandEntities?.[c.id];
  return {
    id: `brand:${c.id}`,
    kind: "brand",
    name: firstNonBlank(entity?.displayName, entity?.name, c.name) ?? `Brand ${c.id}`,
    state,
    statusLabel: d ? d.label : "Included",
    entitlement: d?.entitlement ?? false,
    logo: entity?.logo,
    color: entity?.color,
  };
};

const filterRow = (c: FilterCheck, state: RowState): LedgerRow => {
  const d = state === "blocked" ? describeBlockedFilter(c) : undefined;
  const optionNames = (c.options ?? []).map((o) =>
    displayCase(firstNonBlank(o.label) ?? o.code),
  );
  return {
    id: `filter:${c.code}`,
    kind: "filter",
    name: displayCase(firstNonBlank(c.label) ?? c.code),
    meta: optionNames.length
      ? `${optionNames.length} option${optionNames.length === 1 ? "" : "s"}`
      : undefined,
    state,
    statusLabel: d ? d.label : "Included",
    detail: d?.detail,
    members: state === "blocked" ? undefined : optionNames,
    entitlement: d?.entitlement ?? false,
  };
};

const group = (kind: LedgerKind, rows: LedgerRow[]): LedgerGroup | null => {
  if (rows.length === 0) return null;
  const toAdd = rows.filter((r) => r.state === "adoptable").length;
  const locked = rows.filter((r) => r.state === "blocked").length;
  const quiet = toAdd === 0 && locked === 0;
  const parts: string[] = [];
  if (toAdd) parts.push(`${toAdd} to add`);
  if (locked) parts.push(`${locked} locked`);
  return {
    kind,
    label: NOUN[kind].group,
    note: quiet ? "all included" : parts.join(" · "),
    collapsedByDefault: quiet,
    summary: quiet ? `${countOf(rows.length, kind)} — all included` : undefined,
    rows,
  };
};

/**
 * Flattens the four-bucket partition into ordered, render-ready groups.
 * Actionable rows lead within each group so the eye finds them without
 * scrolling; the segment cap is deliberately not modelled here because
 * disabling depends on live checkbox state, which belongs to the component.
 */
export function buildLedger(
  p: SelectionPartition,
  ctx?: BuildLedgerContext,
): LedgerGroup[] {
  return [
    group("brandSet", [
      ...p.adoptable.brandSets.map((c) => setRow(c, "adoptable")),
      ...p.blocked.brandSets.map((c) => setRow(c, "blocked")),
      ...p.usable.brandSets.map((c) => setRow(c, "included")),
    ]),
    group("segment", [
      ...p.adoptable.segments.map((c) => segmentRow(c, "adoptable")),
      ...p.blocked.segments.map((c) => segmentRow(c, "blocked")),
      ...p.usable.segments.map((c) => segmentRow(c, "included")),
    ]),
    group("brand", [
      ...p.blocked.brands.map((c) => brandRow(c, "blocked", ctx)),
      ...p.usable.brands.map((c) => brandRow(c, "included", ctx)),
    ]),
    group("filter", [
      ...p.blocked.filters.map((c) => filterRow(c, "blocked")),
      ...p.usable.filters.map((c) => filterRow(c, "included")),
    ]),
  ].filter((g): g is LedgerGroup => g !== null);
}
